import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const checkItems = [
  '何をつくるべきか・何が足りないか',
  'ブランドや広告の課題の優先順位',
  '制作範囲・進め方・予算感の大枠',
];

export default function ContactSection() {
  const left = useScrollAnimation();
  const right = useScrollAnimation();
  const items = useStaggerAnimation(checkItems.length);
  const buttons = useScrollAnimation();

  return (
    <section className="texture-contact py-28 md:py-40 bg-warm-light relative overflow-hidden">

      {/* ── 「対話の始まり・収束・焦点」をテーマにした装飾 ── */}

      {/* 右上の「収束する同心円」— 焦点に向かう */}
      <div className="absolute -top-10 -right-10 pointer-events-none">
        <div className="absolute rounded-full border border-accent-amber/18" style={{ width: 160, height: 160, top: 0, right: 0 }} />
        <div className="absolute rounded-full border border-accent-gold/12 hidden sm:block" style={{ width: 280, height: 280, top: -60, right: -60 }} />
        <div className="absolute rounded-full border border-accent-amber/8 hidden md:block" style={{ width: 400, height: 400, top: -120, right: -120 }} />
        {/* 焦点（中心点） */}
        <div className="absolute w-3 h-3 rounded-full bg-accent-amber/30" style={{ top: 79, right: 79 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-amber/50" style={{ top: 80, right: 80 }} />
      </div>

      {/* 右上の「収束する菱形」— 焦点に向かう */}
      <div className="absolute top-5 right-6 lg:top-10 lg:right-12 pointer-events-none">
        <div className="absolute border-2 border-accent-gold/35 rotate-45" style={{ width: 40, height: 40, top: 0, right: 0 }} />
        <div className="absolute border border-accent-amber/42 rotate-45" style={{ width: 20, height: 20, top: 10, right: 10 }} />
        <div className="absolute rounded-full bg-accent-gold/45" style={{ width: 6, height: 6, top: 17, right: 17 }} />
      </div>

      {/* 左下のドットグリッド（大）— 散らばった状態から収束へ */}
      <div className="absolute bottom-0 left-0 w-40 h-32 lg:w-72 lg:h-56 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.25) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
      </div>

      {/* 中央の「収束する矢印群」— 対話に向かう */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
        {/* 四方から中心に向かう細い線 */}
        {[
          { angle: 0, length: 60 },
          { angle: 45, length: 50 },
          { angle: 90, length: 60 },
          { angle: 135, length: 50 },
          { angle: 180, length: 60 },
          { angle: 225, length: 50 },
          { angle: 270, length: 60 },
          { angle: 315, length: 50 },
        ].map(({ angle, length }, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              width: 1,
              height: length,
              background: `rgba(${i % 3 === 0 ? '212,122,46' : i % 3 === 1 ? '201,168,76' : '74,158,142'},0.08)`,
              transformOrigin: 'top center',
              transform: `rotate(${angle}deg) translateX(-50%)`,
              left: '50%',
              top: '50%',
            }}
          />
        ))}
        {/* 中心点 */}
        <div className="absolute w-2 h-2 rounded-full bg-accent-teal/20" style={{ left: -4, top: -4 }} />
      </div>

      {/* 左側縦ライン（セクションラベル） */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
        <span className="block w-px h-10 bg-divider" />
        <span className="text-[10px] font-garamond italic text-muted-light tracking-widest-jp" style={{ writingMode: 'vertical-rl' }}>Contact</span>
        <span className="block w-px h-10 bg-divider" />
      </div>

      {/* 中央の「30分」を示す時計的な装飾 */}
      <div className="absolute top-[35%] right-[40%] pointer-events-none hidden lg:block">
        <div className="w-3 h-3 rounded-full bg-accent-amber/32" />
        <div className="absolute w-px h-5 bg-accent-amber/20" style={{ left: 6, top: 12, transform: 'rotate(30deg)' }} />
      </div>

      {/* 右中央の「整理・収束」を示す水平線群 */}
      <div className="absolute right-16 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-2 mb-4">
            <div
              style={{
                width: 40 - i * 8,
                height: 1,
                background: `rgba(${i === 0 ? '212,122,46' : i === 1 ? '201,168,76' : '74,158,142'},${0.28 - i * 0.04})`,
              }}
            />
            <div
              className="rounded-full"
              style={{
                width: 4,
                height: 4,
                backgroundColor: `rgba(${i === 0 ? '212,122,46' : i === 1 ? '201,168,76' : '74,158,142'},${0.35 - i * 0.05})`,
              }}
            />
          </div>
        ))}
      </div>

      {/* 浮遊ドット */}
      <div className="absolute top-1/2 left-1/2 w-5 h-5 rounded-full bg-accent-teal/18 pointer-events-none" />
      <div className="absolute top-[35%] right-[40%] w-3 h-3 rounded-full bg-accent-amber/32 pointer-events-none" />
      <div className="absolute bottom-[25%] left-[30%] w-4 h-4 rounded-full bg-accent-gold/25 pointer-events-none hidden lg:block" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
          {/* Left */}
          <div
            ref={left.ref as React.RefObject<HTMLDivElement>}
            className="md:col-span-5 transition-all duration-700"
            style={{ opacity: left.isVisible ? 1 : 0, transform: left.isVisible ? 'translateX(0)' : 'translateX(-32px)' }}
          >
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Let&apos;s Start</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug mb-10 text-2xl md:text-3xl">
              まずは、課題と<br />つくるべきものを整理しましょう。
            </h2>
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
              「何から手をつければいいかわからない」で構いません。<br />
              広告・デザイン・Web・映像——どの領域から相談いただいても、<br />
              全体を見渡しながら一緒に整理します。
            </p>
          </div>

          {/* Right */}
          <div className="md:col-span-7">
            <p
              ref={right.ref as React.RefObject<HTMLParagraphElement>}
              className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-6 transition-all duration-700"
              style={{ opacity: right.isVisible ? 1 : 0, transform: right.isVisible ? 'translateY(0)' : 'translateY(16px)' }}
            >
              30分で整理できること
            </p>
            <div
              ref={items.ref as React.RefObject<HTMLDivElement>}
              className="space-y-0 border-t border-divider mb-12"
            >
              {checkItems.map((item, i) => (
                <div
                  key={item}
                  className="flex items-center gap-5 py-5 border-b border-divider transition-all duration-500"
                  style={{
                    opacity: items.visibleCount > i ? 1 : 0,
                    transform: items.visibleCount > i ? 'translateX(0)' : 'translateX(20px)',
                    transitionDelay: `${i * 100}ms`,
                  }}
                >
                  <span className={`block w-1.5 h-1.5 rounded-full flex-shrink-0 ${i === 0 ? 'bg-accent-teal' : i === 1 ? 'bg-accent-amber' : 'bg-accent-gold'}`} />
                  <span className="font-serif-jp text-base font-light text-charcoal tracking-normal-jp">{item}</span>
                </div>
              ))}
            </div>
            <div
              ref={buttons.ref as React.RefObject<HTMLDivElement>}
              className="flex flex-col sm:flex-row gap-4 transition-all duration-700"
              style={{ opacity: buttons.isVisible ? 1 : 0, transform: buttons.isVisible ? 'translateY(0)' : 'translateY(20px)' }}
            >
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                30分相談
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-charcoal text-charcoal text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                資料請求
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-4 py-4 text-sm font-sans-jp text-muted-brown hover:text-accent-teal transition-colors duration-200 whitespace-nowrap group cursor-pointer"
              >
                お問い合わせフォーム
                <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
